/**
 * WestBengalBillPdfDocument.tsx — WBSEDCL B-IDIT HT Bill (VIREON Cortex Estimate)
 * =================================================================================
 * Generates a bill PDF that closely mirrors the official WBSEDCL B-IDIT printed
 * invoices (West Bengal State Electricity Distribution Company Ltd.).
 *
 * Visual reference: sample bills Consumer ID 900007553, MAR 2025 & APR 2025
 * (Invoice 648000181746 and 633500183051).
 *
 * Layout: header with company name + barcode placeholder, consumer info box,
 * meter readings table, energy charge table (Normal/Peak/Off-Peak × PF adj),
 * demand charge block, electricity duty block, other charges, totals,
 * payment note, amount-in-words, signature block.
 *
 * IMPORTANT: This file is 100% independent of BillPdfDocument.tsx (Telangana).
 * Changes here do NOT affect the Telangana bill generation and vice-versa.
 */

import {
  Document, Page, View, Text, StyleSheet, PDFDownloadLink,
} from '@react-pdf/renderer';
import { format, startOfMonth } from 'date-fns';
import type { BillPdfData } from './BillPdfDocument';

// ─── Colour palette (WBSEDCL bills use black/monochrome with thin borders) ────

const BLACK  = '#000000';
const WHITE  = '#FFFFFF';
const GRAY   = '#888888';
const LGRAY  = '#CCCCCC';
const DGRAY  = '#444444';

// ─── Styles ───────────────────────────────────────────────────────────────────

const S = StyleSheet.create({
  page: {
    fontFamily: 'Courier',
    fontSize: 6.5,
    color: BLACK,
    paddingHorizontal: 20,
    paddingVertical: 16,
    backgroundColor: WHITE,
  },

  // ── Outer border
  pageBorder: {
    position: 'absolute',
    top: 8, left: 8, right: 8, bottom: 8,
    borderWidth: 0.5, borderColor: LGRAY,
  },

  // ── Company header
  headerWrap: {
    borderBottomWidth: 1, borderBottomColor: BLACK,
    paddingBottom: 4, marginBottom: 4,
    flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start',
  },
  headerLeft: { flex: 1 },
  companyLine1: {
    fontFamily: 'Courier-Bold', fontSize: 9, color: BLACK,
    textAlign: 'center', marginBottom: 1,
  },
  companyLine2: {
    fontFamily: 'Courier-Bold', fontSize: 7, color: BLACK,
    textAlign: 'center', marginBottom: 1,
  },
  companyLine3: {
    fontFamily: 'Courier', fontSize: 6, color: BLACK,
    textAlign: 'center', marginBottom: 0.5,
  },
  barcodeBox: {
    width: 80, height: 30, borderWidth: 0.5, borderColor: LGRAY,
    alignItems: 'center', justifyContent: 'center', marginLeft: 8,
  },
  barcodeText: { fontFamily: 'Courier', fontSize: 5, color: GRAY },

  // ── 3-column info row at the top
  infoBlock: {
    flexDirection: 'row', borderWidth: 0.5, borderColor: BLACK,
    marginBottom: 3,
  },
  infoCol: { flex: 1, padding: 3, borderRightWidth: 0.5, borderRightColor: BLACK },
  infoColLast: { flex: 1, padding: 3 },
  iLbl: { fontFamily: 'Courier', fontSize: 5.5, color: DGRAY },
  iVal: { fontFamily: 'Courier-Bold', fontSize: 6, color: BLACK, marginBottom: 1.5 },
  iValSm: { fontFamily: 'Courier', fontSize: 5.5, color: BLACK, marginBottom: 1 },

  // ── Section header
  sHdr: {
    backgroundColor: BLACK, paddingHorizontal: 4, paddingVertical: 1.5,
  },
  sHdrText: { fontFamily: 'Courier-Bold', fontSize: 6, color: WHITE },

  // ── Meter readings table
  meterTable: { borderWidth: 0.5, borderColor: BLACK, marginBottom: 3 },
  mHdrRow: {
    flexDirection: 'row', backgroundColor: '#DDDDDD',
    borderBottomWidth: 0.5, borderBottomColor: BLACK,
    paddingVertical: 1.5, paddingHorizontal: 3,
  },
  mRow: {
    flexDirection: 'row', borderTopWidth: 0.3, borderTopColor: LGRAY,
    paddingVertical: 1.5, paddingHorizontal: 3,
  },
  mHdrCell: {
    fontFamily: 'Courier-Bold', fontSize: 5.5, color: BLACK,
    flex: 1, textAlign: 'center',
  },
  mCell: {
    fontFamily: 'Courier', fontSize: 6, color: BLACK,
    flex: 1, textAlign: 'right',
  },
  mCellB: {
    fontFamily: 'Courier-Bold', fontSize: 6, color: BLACK,
    flex: 1, textAlign: 'right',
  },
  mCellLabel: {
    fontFamily: 'Courier', fontSize: 5.5, color: BLACK, width: 85,
  },

  // ── Energy charge table
  ecTable: { borderWidth: 0.5, borderColor: BLACK, marginBottom: 3 },
  ecHdrRow: {
    flexDirection: 'row', backgroundColor: '#DDDDDD',
    borderBottomWidth: 0.5, borderBottomColor: BLACK,
    paddingVertical: 1.5, paddingHorizontal: 3,
  },
  ecRow: {
    flexDirection: 'row', borderTopWidth: 0.3, borderTopColor: LGRAY,
    paddingVertical: 1.5, paddingHorizontal: 3,
  },
  ecRowAlt: {
    flexDirection: 'row', borderTopWidth: 0.3, borderTopColor: LGRAY,
    paddingVertical: 1.5, paddingHorizontal: 3,
    backgroundColor: '#F5F5F5',
  },
  ecHdr: { fontFamily: 'Courier-Bold', fontSize: 5.5, color: BLACK, flex: 1, textAlign: 'center' },
  ecHdrWide: { fontFamily: 'Courier-Bold', fontSize: 5.5, color: BLACK, width: 90 },
  ecCell: { fontFamily: 'Courier', fontSize: 6, color: BLACK, flex: 1, textAlign: 'right' },
  ecCellB: { fontFamily: 'Courier-Bold', fontSize: 6, color: BLACK, flex: 1, textAlign: 'right' },
  ecLabel: { fontFamily: 'Courier', fontSize: 5.5, color: BLACK, width: 90 },
  ecLabelB: { fontFamily: 'Courier-Bold', fontSize: 6, color: BLACK, width: 90 },
  ecTotal: {
    flexDirection: 'row', borderTopWidth: 0.8, borderTopColor: BLACK,
    paddingVertical: 2, paddingHorizontal: 3,
    backgroundColor: '#EEEEEE',
  },

  // ── Charges summary (right pane)
  twoColWrap: { flexDirection: 'row', marginBottom: 3, gap: 4 },
  leftHalf: { flex: 1.5 },
  rightHalf: { flex: 1 },

  // Demand block
  dcBlock: { borderWidth: 0.5, borderColor: BLACK, marginBottom: 3 },
  dcRow: {
    flexDirection: 'row', borderTopWidth: 0.3, borderTopColor: LGRAY,
    paddingVertical: 1.5, paddingHorizontal: 3,
  },
  dcLabel: { fontFamily: 'Courier', fontSize: 6, color: BLACK, flex: 2 },
  dcLabelB: { fontFamily: 'Courier-Bold', fontSize: 6, color: BLACK, flex: 2 },
  dcVal: { fontFamily: 'Courier', fontSize: 6, color: BLACK, flex: 1, textAlign: 'right' },
  dcValB: { fontFamily: 'Courier-Bold', fontSize: 6, color: BLACK, flex: 1, textAlign: 'right' },

  // Other charges & totals summary
  summaryBlock: { borderWidth: 0.5, borderColor: BLACK, marginBottom: 3 },
  sumRow: {
    flexDirection: 'row', justifyContent: 'space-between',
    paddingHorizontal: 4, paddingVertical: 1.5,
    borderBottomWidth: 0.3, borderBottomColor: LGRAY,
  },
  sumRowBold: {
    flexDirection: 'row', justifyContent: 'space-between',
    paddingHorizontal: 4, paddingVertical: 2,
    borderBottomWidth: 0.5, borderBottomColor: BLACK,
    backgroundColor: '#EEEEEE',
  },
  sumLbl: { fontFamily: 'Courier', fontSize: 6, color: BLACK, flex: 1 },
  sumLblB: { fontFamily: 'Courier-Bold', fontSize: 6, color: BLACK, flex: 1 },
  sumVal: { fontFamily: 'Courier', fontSize: 6, color: BLACK, textAlign: 'right' },
  sumValB: { fontFamily: 'Courier-Bold', fontSize: 6.5, color: BLACK, textAlign: 'right' },

  // Payment note
  noteBox: {
    borderWidth: 0.5, borderColor: BLACK, padding: 4, marginBottom: 3,
  },
  noteLine: { fontFamily: 'Courier', fontSize: 5.5, color: BLACK, lineHeight: 1.6, marginBottom: 0.5 },

  // Words
  wordsBox: {
    borderWidth: 0.5, borderColor: BLACK, marginBottom: 3,
    flexDirection: 'row', paddingHorizontal: 5, paddingVertical: 3,
  },
  wordsLbl: { fontFamily: 'Courier-Bold', fontSize: 5.5, color: BLACK, width: 100 },
  wordsVal: { fontFamily: 'Courier', fontSize: 5.5, color: BLACK, flex: 1, lineHeight: 1.4 },

  // Signature row
  sigRow: {
    flexDirection: 'row', justifyContent: 'space-between',
    alignItems: 'flex-end', marginTop: 2,
  },
  sigLeft: { fontFamily: 'Courier', fontSize: 5.5, color: BLACK, flex: 1 },
  sigRight: {
    borderWidth: 0.5, borderColor: BLACK, padding: 5, width: 120, alignItems: 'center',
  },
  sigLine: {
    borderTopWidth: 0.5, borderTopColor: BLACK, width: 80, marginBottom: 2, marginTop: 10,
  },
  sigText: { fontFamily: 'Courier-Bold', fontSize: 6, color: BLACK, textAlign: 'center' },

  // Estimate notice
  estimateNotice: {
    fontFamily: 'Courier-BoldOblique', fontSize: 5, color: GRAY,
    textAlign: 'center', marginTop: 1,
  },

  // Rebate/surcharge highlighting
  rebateVal: { fontFamily: 'Courier', fontSize: 6, color: BLACK, textAlign: 'right' },
  penaltyVal: { fontFamily: 'Courier', fontSize: 6, color: BLACK, textAlign: 'right' },
});

// ─── Helpers ──────────────────────────────────────────────────────────────────

function rs(v: number): string {
  return v.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function fn(v: number, d = 2): string { return v.toFixed(d); }

function numberToWords(n: number): string {
  if (n <= 0) return 'Zero Only';
  const ones  = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven',
                  'Eight', 'Nine', 'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen',
                  'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
  const tens  = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty',
                  'Sixty', 'Seventy', 'Eighty', 'Ninety'];
  function chunk(val: number): string {
    if (!val) return '';
    if (val < 20) return ones[val] + ' ';
    if (val < 100) return tens[Math.floor(val / 10)] + (ones[val % 10] ? ' ' + ones[val % 10] + ' ' : ' ');
    return ones[Math.floor(val / 100)] + ' Hundred ' + chunk(val % 100);
  }
  const cr   = Math.floor(n / 10_000_000);
  const lakh = Math.floor((n % 10_000_000) / 100_000);
  const thou = Math.floor((n % 100_000) / 1_000);
  const rest = Math.floor(n % 1_000);
  let out = '';
  if (cr)   out += chunk(cr)   + 'Crore ';
  if (lakh) out += chunk(lakh) + 'Lakh ';
  if (thou) out += chunk(thou) + 'Thousand ';
  if (rest) out += chunk(rest);
  return out.trim().replace(/\s+/g, ' ') + ' Only';
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function InfoPair({ lbl, val, bold }: { lbl: string; val: string; bold?: boolean }) {
  return (
    <>
      <Text style={S.iLbl}>{lbl}</Text>
      <Text style={bold ? S.iVal : S.iValSm}>{val}</Text>
    </>
  );
}

function SumRow({
  lbl, val, bold,
}: { lbl: string; val: string; bold?: boolean }) {
  return (
    <View style={bold ? S.sumRowBold : S.sumRow}>
      <Text style={bold ? S.sumLblB : S.sumLbl}>{lbl}</Text>
      <Text style={bold ? S.sumValB : S.sumVal}>{val}</Text>
    </View>
  );
}

// ─── Main Document ────────────────────────────────────────────────────────────

export function WestBengalBillPdfDocument({ d }: { d: BillPdfData }) {
  const genDateTime = format(new Date(), 'dd MMM yyyy, hh:mm a');
  const billingCycleLabel = d.billingMonth; // e.g. "JUN, 2026"

  // ─── SINGLE SOURCE OF TRUTH ────────────────────────────────────────────────
  // Every monetary amount shown on the PDF must match the dashboard
  // "Projected Bill" section exactly.  d.projectedNetPayable is the authoritative
  // month-end projected net payable — the same value the dashboard card shows.
  // All other projected line items come from the matching projected breakdown
  // fields passed from Billings.tsx, which are computed by the WB billing engine.
  // ───────────────────────────────────────────────────────────────────────────

  // Meter factor
  const mf = d.multiplyingFactor || 2;

  // ── MTD kWh by period — billing engine source (= "This Month" dashboard) ──
  // These are the ACTUAL MTD kWh accumulated to date. Used ONLY for:
  //   • Previous Reading = 0 (start of month)
  //   • Deriving projected values below
  // NOT used directly in the charge table — projected values are used there.
  const kwhNormalMtd  = (d.wbKwhNormal  != null && d.wbKwhNormal  > 0)
    ? d.wbKwhNormal
    : (d.energyChargeNormal  > 0 ? d.energyChargeNormal  / (d.rateNormal  || 6.83) : (d.kwhNormal  ?? 0));
  const kwhPeakMtd    = (d.wbKwhPeak    != null && d.wbKwhPeak    > 0)
    ? d.wbKwhPeak
    : (d.energyChargePeak    > 0 ? d.energyChargePeak    / (d.ratePeak    || 8.20) : (d.kwhPeak    ?? 0));
  const kwhOffpeakMtd = (d.wbKwhOffpeak != null && d.wbKwhOffpeak > 0)
    ? d.wbKwhOffpeak
    : (d.energyChargeOffpeak > 0 ? d.energyChargeOffpeak / (d.rateOffpeak || 5.12) : (d.kwhOffpeak ?? 0));

  // ── Projected kWh per period (linear extrapolation to full month) ─────────
  // These are the SAME values the dashboard "Projected Bill" uses.
  // PDF must show projected values so it matches the Projected Bill exactly.
  const daysPassed  = d.daysPassed  || 1;
  const daysInMonth = d.daysInMonth || 30;
  const projFactor  = daysInMonth / daysPassed;

  const kwhNormal  = kwhNormalMtd  * projFactor;   // projected full-month Normal kWh
  const kwhPeak    = kwhPeakMtd    * projFactor;   // projected full-month Peak kWh
  const kwhOffpeak = kwhOffpeakMtd * projFactor;   // projected full-month Off-Peak kWh
  const totalKwh   = kwhNormal + kwhPeak + kwhOffpeak;

  // ── Energy charge rates (WBSEDCL B-IDIT) ────────────────────────────────
  const rateNormal  = d.rateNormal  || 6.83;
  const ratePeak    = d.ratePeak    || 8.20;
  const rateOffpeak = d.rateOffpeak || 5.12;

  // ── Projected energy charges per period ──────────────────────────────────
  // Projected EC = projected kWh × rate
  // This makes: Normal EC + Peak EC + Off-Peak EC == Projected energy charge (gross)
  // matching the dashboard Projected Bill "Projected energy charge (gross)" exactly.
  const ecNormal  = kwhNormal  * rateNormal;
  const ecPeak    = kwhPeak    * ratePeak;
  const ecOffpeak = kwhOffpeak * rateOffpeak;
  // Use d.projectedGrossEcRs as the authoritative total (from billing engine projection)
  // so the header "Energy/Min Charge" and "Total EC" rows exactly match the dashboard.
  const energyGross = (d.projectedGrossEcRs && d.projectedGrossEcRs > 0)
    ? d.projectedGrossEcRs
    : ecNormal + ecPeak + ecOffpeak;

  // ── PF adjustment % per period (same slabs — applied to projected EC) ────
  const pfPctNorm  = d.pfRebatePctNormal  ?? 0;
  const pfPctPeak  = d.pfRebatePctPeak    ?? 0;
  const pfPctOff   = d.pfRebatePctOffpeak ?? 0;
  // PF Rs = projected EC × PF% (matches dashboard projected PF rebate)
  const pfRsNorm   = ecNormal  * (pfPctNorm  / 100);
  const pfRsPeak   = ecPeak    * (pfPctPeak  / 100);
  const pfRsOff    = ecOffpeak * (pfPctOff   / 100);

  // ── PF value per period — "This Month" per-period PF from billing engine ──
  const pfNorm  = (d.wbPfAvgNormal  != null && d.wbPfAvgNormal  > 0) ? d.wbPfAvgNormal  : (d.pfAvg || 0.99);
  const pfPeak  = (d.wbPfAvgPeak    != null && d.wbPfAvgPeak    > 0) ? d.wbPfAvgPeak    : (d.pfAvg || 0.99);
  const pfOff   = (d.wbPfAvgOffpeak != null && d.wbPfAvgOffpeak > 0) ? d.wbPfAvgOffpeak : (d.pfAvg || 0.99);

  // ── LF rate for table display — derive from projected totalKwh ───────────
  const lfPct        = d.lfPct ?? 0;
  const lfRateRsKwh  = totalKwh > 0 && d.lfRebateCost != null
    ? Math.abs(d.lfRebateCost) / kwhNormalMtd  // rate per kWh from MTD amounts (rate is fixed)
    : 0;

  // ── Demand charges (from dashboard billing-cycle peak values) ────────────
  const chargeableKva = d.chargeableKva ?? d.normalKva ?? 0;
  const dcNormal      = d.demandChargeRs      ?? (chargeableKva * 270);
  const addlKva       = d.mdOverrunKva        ?? d.addlKva ?? 0;
  const dcAddl        = d.mdOverrunPenaltyRs  ?? (addlKva * 135);

  // ── Per-period Max Demand kVA — use wbMdKva* (billing engine) when available ──
  // wbMdKvaNormal/Peak/Offpeak come from breakdown.mdKvaNormal/Peak/Offpeak,
  // the same values shown on the dashboard "Max Demand by Time-of-Day Period" display.
  // Falls back to maxDemandKvaNormal/Peak/Offpeak (Pi accumulator) or overall maxDemandKva.
  // All branches guaranteed to resolve to `number` via `?? 0` guards.
  const mdKvaNormal: number  = (d.wbMdKvaNormal  != null && d.wbMdKvaNormal  > 0)
    ? d.wbMdKvaNormal
    : ((d.maxDemandKvaNormal  ?? 0) > 0 ? (d.maxDemandKvaNormal  ?? 0) : (d.maxDemandKva ?? chargeableKva));
  const mdKvaPeak: number    = (d.wbMdKvaPeak    != null && d.wbMdKvaPeak    > 0)
    ? d.wbMdKvaPeak
    : ((d.maxDemandKvaPeak    ?? 0) > 0 ? (d.maxDemandKvaPeak    ?? 0) : (d.maxDemandKva ?? chargeableKva));
  const mdKvaOffpeak: number = (d.wbMdKvaOffpeak != null && d.wbMdKvaOffpeak > 0)
    ? d.wbMdKvaOffpeak
    : ((d.maxDemandKvaOffpeak ?? 0) > 0 ? (d.maxDemandKvaOffpeak ?? 0) : (d.maxDemandKva ?? chargeableKva));

  // ── Meter readings table ─────────────────────────────────────────────────
  // This is a PROJECTED bill estimate, so all reading values are projected:
  //   Previous Reading = 0.000 (start of billing month — always zero for CORTEX estimate)
  //   Present Reading  = projected full-month kWh (extrapolated to month-end)
  //   Reading Advance  = Present − Previous = projected kWh (matches Projected Bill)
  // This ensures: Normal kWh × ₹6.83 + Peak × ₹8.20 + Off-Peak × ₹5.12
  //               == Projected energy charge (gross) on the dashboard exactly.
  const presentKwhNormal  = kwhNormal;   // projected full-month
  const presentKwhPeak    = kwhPeak;
  const presentKwhOffpeak = kwhOffpeak;
  // Previous is always 0 for CORTEX month-to-date estimate (billing starts from 1st)
  const prevKwhNormal  = 0;
  const prevKwhPeak    = 0;
  const prevKwhOffpeak = 0;

  // ── Projected kVAh per period — kVAh = kWh ÷ PF (per period) ────────────
  // kVAh (apparent energy) ≠ kWh (active energy). Relationship: PF = kWh / kVAh
  // → kVAh = kWh / PF
  //
  // PF source: d.wbPfAvgNormal/Peak/Offpeak — these are now set from
  // latest?.runningAvgPFNormal/Peak/Offpeak (derivedEngine fields computed as
  // ΣWhNormal / ΣVAhNormal etc.), which give the correct per-period billing-cycle PF.
  // Fall back chain: per-period → overall runningAvgPF → d.pfAvg → 0.96.
  // Clamp to (0, 1] to guard against any corrupt values.
  const clampPf = (pf: number) => (pf > 0 && pf <= 1) ? pf : (d.pfAvg > 0 && d.pfAvg <= 1 ? d.pfAvg : 0.96);
  const pfKvahNormal  = clampPf(d.wbPfAvgNormal  ?? 0);
  const pfKvahPeak    = clampPf(d.wbPfAvgPeak    ?? 0);
  const pfKvahOffpeak = clampPf(d.wbPfAvgOffpeak ?? 0);

  const presentKvahNormal  = presentKwhNormal  / pfKvahNormal;
  const presentKvahPeak    = presentKwhPeak    / pfKvahPeak;
  const presentKvahOffpeak = presentKwhOffpeak / pfKvahOffpeak;

  // ── PROJECTED line-item values — match the dashboard Projected Bill card ─
  // projectedPfAdjRs: positive = rebate (reduces bill), negative = surcharge
  const projPfAdj    = d.projectedPfAdjRs  ?? 0;  // positive = rebate
  // projectedLfAdjRs: positive = rebate, negative = surcharge/penalty
  const projLfAdj    = d.projectedLfAdjRs  ?? 0;  // positive = rebate, negative = penalty
  const projEdRs     = d.projectedEdRs     ?? 0;

  // ── PROJECTED totals — single source of truth from the dashboard ─────────
  const projectedPayable = d.projectedNetPayable;

  // Meter rental (fixed at ₹1,200/month — not pro-rated for projection period)
  const meterRental = d.meterRentalRs ?? 1200;
  const edPct       = 12.5;

  // Projected gross (before TPR) = netPayable ÷ (1 − tprFraction)
  const tprPct              = 0.89;
  const projGrossCurrentMonth = projectedPayable / (1 - tprPct / 100);
  const tprAmount             = projGrossCurrentMonth - projectedPayable;

  const payableByDueDate    = projectedPayable;
  const payableAfterDueDate = projGrossCurrentMonth;

  // MTD Total EC = sum of per-period charges — matches dashboard Energy Charges card.
  // projGrossEc is projected (full-month), energyMinCharge must be MTD for current display.
  const energyMinCharge = energyGross;

  // EDIM units = projected full-month total kWh (kwhNormal/Peak/Offpeak are already projected)
  const edimUnits = totalKwh;

  // ── Date labels ──────────────────────────────────────────────────────────
  // Present Reading Date  = current data end date (d.payableDate)
  // Previous Reading Date = first day of billing month (from wbPreviousReadingDate)
  const presentReadingDate  = d.payableDate;
  const previousReadingDate = d.wbPreviousReadingDate
    ?? format(startOfMonth(new Date()), 'dd-MMM-yyyy');

  return (
    <Document
      title={`CORTEX WBSEDCL Bill — ${billingCycleLabel}`}
      author="VIREON Cortex Energy Monitor"
      subject="WBSEDCL B-IDIT Bill Estimate (Month-to-Date)"
    >
      <Page size="A4" style={S.page}>

        {/* ── Header ─────────────────────────────────────────────────── */}
        <View style={S.headerWrap}>
          <View style={S.headerLeft}>
            <Text style={S.companyLine1}>West Bengal State Electricity Distribution Company Ltd.</Text>
            <Text style={S.companyLine2}>(A Government of West Bengal Enterprise)</Text>
            <Text style={S.companyLine3}>24-PARGANAS (NORTH) REGIONAL OFFICE</Text>
            <Text style={S.companyLine3}>
              OFFICE OF THE REGIONAL MANAGER, ADMINISTRATIVE BUILDING, B.T.ROAD, VIVEK NAGAR, KHARDAH, PIN-700119
            </Text>
            <Text style={S.companyLine3}>
              Phone: 2592-0353/0591 · Fax: 2593-1841 · Email: rm.north24pgs@wbsedcl.in · TAN:CALW05053G
            </Text>
            <Text style={S.estimateNotice}>
              * SYSTEM-GENERATED MONTH-TO-DATE ESTIMATE — NOT AN OFFICIAL WBSEDCL INVOICE *
            </Text>
          </View>
          <View style={S.barcodeBox}>
            <Text style={S.barcodeText}>|||||||||||||||||</Text>
            <Text style={S.barcodeText}>||| ||| ||| ||||</Text>
            <Text style={S.barcodeText}>|||||||||||||||||</Text>
          </View>
        </View>

        {/* ── Consumer Info + Invoice Info + Tariff Info ──────────────── */}
        <View style={S.infoBlock}>
          {/* Left: Consumer details */}
          <View style={S.infoCol}>
            <InfoPair lbl="Consumer ID:"       val={d.consumerId}         bold />
            <InfoPair lbl="Installation No:"   val={d.installationNo}     bold />
            <Text style={[S.iVal, { marginTop: 2 }]}>{d.consumerName}</Text>
            <Text style={S.iValSm}>{d.serviceAddress}</Text>
            <InfoPair lbl="Country:" val="India" />
          </View>

          {/* Middle: Invoice details */}
          <View style={S.infoCol}>
            <InfoPair lbl="Invoice No.:"               val={d.invoiceNo}          bold />
            <InfoPair lbl="Billing Date:"               val={d.billingDate}        bold />
            <InfoPair lbl="Billing Cycle:"              val={billingCycleLabel}    bold />
            <InfoPair lbl="Present Reading Date:"       val={presentReadingDate}   />
            <InfoPair lbl="Previous Reading Date:"      val={previousReadingDate}  />
            <InfoPair lbl="Service At:"                 val={d.serviceAddress}     />
            <InfoPair lbl="BILL No:"                    val={'000' + d.consumerId} />
            <InfoPair lbl="Account Reference No:"       val={d.installationNo}     />
            <InfoPair lbl="Consumer No:"                val={'000' + d.consumerId} />
          </View>

          {/* Right: Tariff details */}
          <View style={S.infoColLast}>
            <InfoPair lbl="Tariff Code:"            val={d.tariffCode || 'B-IDIT'} bold />
            <InfoPair lbl="Supply Voltage(KV):"     val={d.supplyVoltageKv || '0.40'} />
            <InfoPair lbl="Contract Demand(KVA):"   val={fn(d.contractDemandKva, 2)} />
            <InfoPair lbl="Solar PV Capacity(Kwp):" val="—"                        />
            <InfoPair lbl="PF:"                     val={fn(d.pfAvg || 0.99, 4)}   />
            <InfoPair lbl="LF%:"                    val={fn(lfPct, 4)}             />
            <InfoPair lbl="Nature Of Industry:"     val="MISCELLANEOUS INDUSTRIES"  />
            <InfoPair lbl="Pan No:"                 val={d.panNo || '—'}            />
          </View>
        </View>

        {/* ── Meter Info ──────────────────────────────────────────────── */}
        <View style={{ borderWidth: 0.5, borderColor: BLACK, marginBottom: 3, padding: 3 }}>
          <View style={{ flexDirection: 'row', marginBottom: 2 }}>
            <Text style={[S.iLbl, { width: 50 }]}>Meter No</Text>
            <Text style={[S.iVal, { width: 70 }]}>{d.meterNo || '—'}</Text>
            <Text style={[S.iLbl, { width: 30 }]}>Type</Text>
            <Text style={[S.iVal, { width: 30 }]}>TOD</Text>
            <Text style={[S.iLbl, { width: 20 }]}>MC</Text>
            <Text style={[S.iVal, { width: 15 }]}>1</Text>
            <Text style={[S.iLbl, { width: 15 }]}>MF</Text>
            <Text style={[S.iVal, { width: 30 }]}>{fn(mf, 3)}</Text>
            <Text style={[S.iLbl, { width: 50 }]}>Loss Factor</Text>
            <Text style={[S.iVal, { width: 15 }]}>1</Text>
            <Text style={[S.iLbl, { width: 40 }]}>Net MF</Text>
            <Text style={[S.iVal, { flex: 1 }]}>{fn(mf, 2)}</Text>
          </View>
        </View>

        {/* ── Meter Readings Table ─────────────────────────────────────── */}
        <View style={S.meterTable}>
          {/* Header row */}
          <View style={S.mHdrRow}>
            <Text style={[S.mHdrCell, { width: 55, textAlign: 'left' }]}>Meter Readings</Text>
            <Text style={[S.mHdrCell, { flex: 1 }]}>KVAH</Text>
            <Text style={[S.mHdrCell, { flex: 1 }]}> </Text>
            <Text style={[S.mHdrCell, { flex: 1 }]}> </Text>
            <Text style={[S.mHdrCell, { flex: 1 }]}>KWH</Text>
            <Text style={[S.mHdrCell, { flex: 1 }]}> </Text>
            <Text style={[S.mHdrCell, { flex: 1 }]}> </Text>
            <Text style={[S.mHdrCell, { flex: 1 }]}>KVA / Dt. &amp; Time of MD</Text>
            <Text style={[S.mHdrCell, { flex: 1 }]}> </Text>
            <Text style={[S.mHdrCell, { flex: 1 }]}> </Text>
          </View>
          <View style={S.mHdrRow}>
            <Text style={[S.mHdrCell, { width: 55, textAlign: 'left' }]}>Time</Text>
            <Text style={S.mHdrCell}>Normal</Text>
            <Text style={S.mHdrCell}>Peak</Text>
            <Text style={S.mHdrCell}>Off-peak</Text>
            <Text style={S.mHdrCell}>Normal</Text>
            <Text style={S.mHdrCell}>Peak</Text>
            <Text style={S.mHdrCell}>Off-peak</Text>
            <Text style={S.mHdrCell}>Normal</Text>
            <Text style={S.mHdrCell}>Peak</Text>
            <Text style={S.mHdrCell}>Off-peak</Text>
          </View>
          {/* Present row — KVAH = projected kVAh (kWh÷PF); KWH = projected kWh */}
          <View style={S.mRow}>
            <Text style={[S.mCellLabel, { width: 55 }]}>Present</Text>
            {/* KVAH columns — kVAh = projected kWh ÷ per-period PF */}
            <Text style={S.mCell}>{fn(presentKvahNormal  / mf, 3)}</Text>
            <Text style={S.mCell}>{fn(presentKvahPeak    / mf, 3)}</Text>
            <Text style={S.mCell}>{fn(presentKvahOffpeak / mf, 3)}</Text>
            {/* KWH columns — projected kWh */}
            <Text style={S.mCell}>{fn(presentKwhNormal  / mf, 3)}</Text>
            <Text style={S.mCell}>{fn(presentKwhPeak    / mf, 3)}</Text>
            <Text style={S.mCell}>{fn(presentKwhOffpeak / mf, 3)}</Text>
            <Text style={S.mCell}>{fn(mdKvaNormal,  3)}</Text>
            <Text style={S.mCell}>{fn(mdKvaPeak,    3)}</Text>
            <Text style={S.mCell}>{fn(mdKvaOffpeak, 3)}</Text>
          </View>
          {/* Previous row — reading at billing month start = Present − MTD */}
          <View style={S.mRow}>
            <Text style={[S.mCellLabel, { width: 55 }]}>Previous</Text>
            <Text style={S.mCell}>{fn(prevKwhNormal  / mf, 3)}</Text>
            <Text style={S.mCell}>{fn(prevKwhPeak    / mf, 3)}</Text>
            <Text style={S.mCell}>{fn(prevKwhOffpeak / mf, 3)}</Text>
            <Text style={S.mCell}>{fn(prevKwhNormal  / mf, 3)}</Text>
            <Text style={S.mCell}>{fn(prevKwhPeak    / mf, 3)}</Text>
            <Text style={S.mCell}>{fn(prevKwhOffpeak / mf, 3)}</Text>
            <Text style={S.mCell}> </Text>
            <Text style={S.mCell}> </Text>
            <Text style={S.mCell}> </Text>
          </View>
          {/* Reading Advance = Present − Previous */}
          {/* KVAH columns: projected kVAh; KWH columns: projected kWh */}
          <View style={[S.mRow, { backgroundColor: '#F0F0F0' }]}>
            <Text style={[S.mCellLabel, { width: 55 }]}>Reading Advance</Text>
            {/* KVAH columns — kVAh = projected kWh ÷ per-period PF */}
            <Text style={S.mCellB}>{fn(presentKvahNormal  / mf, 3)}</Text>
            <Text style={S.mCellB}>{fn(presentKvahPeak    / mf, 3)}</Text>
            <Text style={S.mCellB}>{fn(presentKvahOffpeak / mf, 3)}</Text>
            {/* KWH columns — projected kWh */}
            <Text style={S.mCellB}>{fn(kwhNormal  / mf, 3)}</Text>
            <Text style={S.mCellB}>{fn(kwhPeak    / mf, 3)}</Text>
            <Text style={S.mCellB}>{fn(kwhOffpeak / mf, 3)}</Text>
            <Text style={S.mCellB}>{fn(mdKvaNormal  * mf, 3)}</Text>
            <Text style={S.mCellB}>{fn(mdKvaPeak    * mf, 3)}</Text>
            <Text style={S.mCellB}>{fn(mdKvaOffpeak * mf, 3)}</Text>
          </View>
        </View>

        {/* ── Energy Charge Table ──────────────────────────────────────── */}
        <View style={S.ecTable}>
          <View style={S.ecHdrRow}>
            <Text style={[S.ecHdrWide, { width: 95 }]}>Energy Charge</Text>
            <Text style={S.ecHdr}>Normal</Text>
            <Text style={S.ecHdr}>Peak</Text>
            <Text style={S.ecHdr}>Off-peak</Text>
            <Text style={[S.ecHdr, { width: 90 }]}>Energy/Min Charge(Rs)</Text>
            <Text style={[S.ecCellB, { width: 60 }]}>{rs(energyMinCharge)}</Text>
          </View>

          {/* Rate row */}
          <View style={S.ecRow}>
            <Text style={[S.ecLabel, { width: 95 }]}>Rate     EC(p/KWH)</Text>
            <Text style={S.ecCell}>{Math.round(rateNormal  * 100)}</Text>
            <Text style={S.ecCell}>{Math.round(ratePeak    * 100)}</Text>
            <Text style={S.ecCell}>{Math.round(rateOffpeak * 100)}</Text>
            <Text style={[S.ecCell, { width: 90 }]}>Rebate on EC(Rs.)</Text>
            <Text style={[S.ecCell, { width: 60 }]}>0.00</Text>
          </View>

          {/* PF rebate/surcharge % */}
          <View style={S.ecRowAlt}>
            <Text style={[S.ecLabel, { width: 95 }]}>P.F.Reb(-)/Sur(+)(%onEC)</Text>
            <Text style={S.ecCell}>{pfPctNorm > 0 ? '-' : ''}{Math.abs(pfPctNorm).toFixed(2)}</Text>
            <Text style={S.ecCell}>{pfPctPeak > 0 ? '-' : ''}{Math.abs(pfPctPeak).toFixed(2)}</Text>
            <Text style={S.ecCell}>{pfPctOff  > 0 ? '-' : ''}{Math.abs(pfPctOff).toFixed(2)}</Text>
            <Text style={[S.ecCell, { width: 90 }]}>Addl.EC(Rs.)</Text>
            <Text style={[S.ecCell, { width: 60 }]}>0.00</Text>
          </View>

          {/* LF rebate */}
          <View style={S.ecRow}>
            <Text style={[S.ecLabel, { width: 95 }]}>L.F.Reb(-)/Sur(+)(p/KWH)</Text>
            <Text style={S.ecCell}>{lfRateRsKwh > 0 ? fn(lfRateRsKwh * 100, 0) : '0'}</Text>
            <Text style={S.ecCell}> </Text>
            <Text style={S.ecCell}> </Text>
            <Text style={[S.ecCell, { width: 90 }]}>Total EC(Rs.)</Text>
            <Text style={[S.ecCellB, { width: 60 }]}>{rs(energyMinCharge)}</Text>
          </View>

          {/* Chargeable kWh */}
          <View style={S.ecRowAlt}>
            <Text style={[S.ecLabel, { width: 95 }]}>Chargeable  KWH</Text>
            <Text style={S.ecCell}>{fn(kwhNormal, 3)}</Text>
            <Text style={S.ecCell}>{fn(kwhPeak, 3)}</Text>
            <Text style={S.ecCell}>{fn(kwhOffpeak, 3)}</Text>
            <Text style={[S.ecCell, { width: 90 }]}> </Text>
            <Text style={[S.ecCell, { width: 60 }]}> </Text>
          </View>

          {/* !EC Amount */}
          <View style={S.ecRow}>
            <Text style={[S.ecLabel, { width: 95 }]}>!EC Amount Rs.</Text>
            <Text style={S.ecCell}>{rs(ecNormal)}</Text>
            <Text style={S.ecCell}>{rs(ecPeak)}</Text>
            <Text style={S.ecCell}>{rs(ecOffpeak)}</Text>
            <Text style={[S.ecCell, { width: 90 }]}> </Text>
            <Text style={[S.ecCell, { width: 60 }]}> </Text>
          </View>

          {/* Chargeable PF */}
          <View style={S.ecRowAlt}>
            <Text style={[S.ecLabel, { width: 95 }]}>Chargeable PF</Text>
            <Text style={S.ecCell}>{fn(pfNorm, 4)}</Text>
            <Text style={S.ecCell}>{fn(pfPeak, 4)}</Text>
            <Text style={S.ecCell}>{fn(pfOff,  4)}</Text>
            <Text style={[S.ecCell, { width: 90 }]}> </Text>
            <Text style={[S.ecCell, { width: 60 }]}> </Text>
          </View>

          {/* PF Rs adjustment */}
          <View style={S.ecRow}>
            <Text style={[S.ecLabel, { width: 95 }]}>PF  Rs.</Text>
            <Text style={S.ecCell}>{pfRsNorm  !== 0 ? (pfRsNorm  > 0 ? '-' : '+') + rs(Math.abs(pfRsNorm))  : '0.00'}</Text>
            <Text style={S.ecCell}>{pfRsPeak  !== 0 ? (pfRsPeak  > 0 ? '-' : '+') + rs(Math.abs(pfRsPeak))  : '0.00'}</Text>
            <Text style={S.ecCell}>{pfRsOff   !== 0 ? (pfRsOff   > 0 ? '-' : '+') + rs(Math.abs(pfRsOff))   : '0.00'}</Text>
            <Text style={[S.ecCell, { width: 90 }]}> </Text>
            <Text style={[S.ecCell, { width: 60 }]}> </Text>
          </View>
        </View>

        {/* ── Demand Charge + Summary (2 columns) ─────────────────────── */}
        <View style={S.twoColWrap}>
          {/* LEFT: Demand charges */}
          <View style={S.leftHalf}>
            <View style={S.dcBlock}>
              <View style={S.sHdr}><Text style={S.sHdrText}>Demand Charge</Text></View>
              <View style={S.dcRow}>
                <Text style={S.dcLabel}>Rate Normal(Rs/KVA/month)</Text>
                <Text style={S.dcVal}>270.00</Text>
                <Text style={[S.dcLabelB, { width: 80 }]}>*Demand Charge</Text>
                <Text style={S.dcValB}>{rs(dcNormal)}</Text>
              </View>
              <View style={S.dcRow}>
                <Text style={S.dcLabel}>Chargeable Normal KVA</Text>
                <Text style={S.dcVal}>{fn(chargeableKva, 2)}</Text>
                <Text style={[S.dcLabel, { width: 80 }]}>Addl.DC(Rs.)</Text>
                <Text style={S.dcVal}>{rs(dcAddl)}</Text>
              </View>
              <View style={S.dcRow}>
                <Text style={S.dcLabel}>   Addl.KVA</Text>
                <Text style={S.dcVal}>{fn(addlKva, 2)}</Text>
                <Text style={[S.dcLabelB, { width: 80 }]}>Total DC(Rs.)</Text>
                <Text style={S.dcValB}>{rs(dcNormal + dcAddl)}</Text>
              </View>
              <View style={[S.dcRow, { borderTopWidth: 0.5, borderTopColor: BLACK }]}>
                <Text style={S.dcLabel}>Rebate(-)/Surcharge(+) (Rs.)</Text>
                <Text style={S.dcVal}> </Text>
                <Text style={[S.dcLabel, { width: 80 }]}>Reb on DC(on TP)</Text>
                <Text style={S.dcVal}> </Text>
              </View>
              <View style={S.dcRow}>
                <Text style={S.dcLabel}>LF Reb(-)/Sur(+)</Text>
                <Text style={S.dcVal}>{projLfAdj !== 0 ? (projLfAdj > 0 ? '-' : '+') + rs(Math.abs(projLfAdj)) : '—'}</Text>
                <Text style={[S.dcLabel, { width: 80 }]}> </Text>
                <Text style={S.dcVal}> </Text>
              </View>
              <View style={S.dcRow}>
                <Text style={S.dcLabel}>PF Reb(-)/Sur(+)</Text>
                <Text style={S.dcVal}>
                  {projPfAdj !== 0 ? (projPfAdj > 0 ? '-' : '+') + rs(Math.abs(projPfAdj)) : '—'}
                </Text>
                <Text style={[S.dcLabel, { width: 80 }]}> </Text>
                <Text style={S.dcVal}> </Text>
              </View>
              <View style={S.dcRow}>
                <Text style={S.dcLabel}>MVCA Charges @0.00 Paise/KWH</Text>
                <Text style={S.dcVal}>0.00</Text>
                <Text style={[S.dcLabel, { width: 80 }]}> </Text>
                <Text style={S.dcVal}> </Text>
              </View>
              <View style={S.dcRow}>
                <Text style={S.dcLabel}>###Government subsidy</Text>
                <Text style={S.dcVal}> </Text>
                <Text style={[S.dcLabel, { width: 80 }]}> </Text>
                <Text style={S.dcVal}> </Text>
              </View>
              <View style={S.dcRow}>
                <Text style={S.dcLabel}>## Other Arrear Charges</Text>
                <Text style={S.dcVal}>0.00</Text>
                <Text style={[S.dcLabel, { width: 80 }]}> </Text>
                <Text style={S.dcVal}> </Text>
              </View>
              <View style={S.dcRow}>
                <Text style={S.dcLabel}>**Revenue Sharing Charge + GST</Text>
                <Text style={S.dcVal}> </Text>
                <Text style={[S.dcLabel, { width: 80 }]}> </Text>
                <Text style={S.dcVal}> </Text>
              </View>
            </View>

            {/* Electricity Duty block — single consolidated row */}
            <View style={S.dcBlock}>
              <View style={S.sHdr}><Text style={S.sHdrText}>Electricity Duty</Text></View>
              <View style={S.dcRow}>
                <Text style={S.dcLabel}>Projected Units (kWh)</Text>
                <Text style={S.dcVal}>{fn(edimUnits, 2)}</Text>
                <Text style={[S.dcLabel, { width: 50 }]}>Rate</Text>
                <Text style={S.dcVal}>{edPct.toFixed(2)}% of bill</Text>
              </View>
              <View style={[S.dcRow, { borderTopWidth: 0.5, borderTopColor: BLACK }]}>
                <Text style={[S.dcLabelB, { flex: 3 }]}>Electricity Duty (Rs.)</Text>
                <Text style={[S.dcValB, { flex: 1 }]}>{rs(projEdRs)}</Text>
              </View>
            </View>
          </View>

          {/* RIGHT: Charges & Payable Summary */}
          <View style={S.rightHalf}>
            <View style={S.summaryBlock}>
              <View style={S.sHdr}><Text style={S.sHdrText}>Charges &amp; Payable Summary (Rs.)</Text></View>
              <SumRow lbl="Rental of Meter/Meters"        val={rs(meterRental)}           />
              <SumRow lbl="LPSC Charges"                  val="0.00"                       />

              <View style={{ height: 6 }} />

              <SumRow lbl="Amnt. For Current Month(Rs)"   val={rs(projGrossCurrentMonth)}  bold />
              <SumRow lbl="Due Date"                      val={d.payableDate}               />
              <SumRow lbl="Payable by DueDate(Rs)"        val={rs(payableByDueDate)}        bold />
              <SumRow lbl="Payable After DueDate(Rs)"     val={rs(payableAfterDueDate)}     />

              <View style={{ height: 4 }} />
              <View style={S.sumRow}>
                <Text style={S.sumLbl}>Timely Payment Rebate ({tprPct}%)</Text>
                <Text style={S.sumVal}>-{rs(tprAmount)}</Text>
              </View>
              <SumRow lbl="Total Timely Payment Rebate"   val={'-' + rs(tprAmount)}        bold />
            </View>
          </View>
        </View>

        {/* ── Cortex estimate notes ────────────────────────────────────── */}
        <View style={S.noteBox}>
          <Text style={[S.noteLine, { marginBottom: 2 }]}>
            {'* This is a CORTEX system-generated Month-to-Date estimate for '}
            {billingCycleLabel}
            {'. Generated: ' + genDateTime + '. The official WBSEDCL invoice supersedes this document.'}
          </Text>
          <Text style={S.noteLine}>
            {'* Energy charges: Normal Rs.' + fn(rateNormal, 2) + '/kWh · Peak Rs.' + fn(ratePeak, 2) + '/kWh · Off-Peak Rs.' + fn(rateOffpeak, 2) + '/kWh per WBSEDCL B-IDIT tariff.'}
          </Text>
          <Text style={S.noteLine}>
            {'* Demand Charge: Rs.270/kVA x ' + fn(chargeableKva, 0) + ' kVA (chargeable)' +
              (addlKva > 0 ? ' + Rs.135/kVA x ' + fn(addlKva, 0) + ' kVA (excess above CD).' : '.')}
          </Text>
          <Text style={S.noteLine}>
            {'* Electricity Duty: ' + edPct.toFixed(1) + '% of net charge = Rs.' + rs(projEdRs) + '. Meter Rental: Rs.' + rs(meterRental) + '/month. TPR: ' + tprPct + '% if paid by due date.'}
          </Text>
        </View>

        {/* ── Amount in words ───────────────────────────────────────────── */}
        <View style={S.wordsBox}>
          <Text style={S.wordsLbl}>Amount Before Due Date(Rs)</Text>
          <Text style={S.wordsVal}>{numberToWords(Math.round(payableByDueDate))}</Text>
        </View>
        <View style={S.wordsBox}>
          <Text style={S.wordsLbl}>Amount After Due date(Rs)</Text>
          <Text style={S.wordsVal}>{numberToWords(Math.round(payableAfterDueDate))}</Text>
        </View>

        {/* ── Signature ─────────────────────────────────────────────────── */}
        <View style={S.sigRow}>
          <Text style={S.sigLeft}>
            {'Messages to consumer: Register your mobile No. and email Id at www.wbsedcl.in\nAs a Go Green initiative, you may opt for the E-Bill facility instead of Paper bill.'}
          </Text>
          <View style={S.sigRight}>
            <View style={S.sigLine} />
            <Text style={S.sigText}>Superintending Engineer</Text>
            <Text style={[S.sigText, { fontFamily: 'Courier' }]}>Divisional Engineer</Text>
            <Text style={[S.sigText, { fontFamily: 'Courier', fontSize: 5 }]}>WBSEDCL / CORTEX Estimate</Text>
          </View>
        </View>

      </Page>
    </Document>
  );
}

// ─── Download Button (West Bengal) ───────────────────────────────────────────

export function WestBengalBillPdfDownloadButton({ data }: { data: BillPdfData }) {
  const filename = 'CORTEX_WBSEDCL_Bill_' + data.billingMonth.replace(/[\s,]+/g, '_') + '.pdf';

  return (
    <PDFDownloadLink document={<WestBengalBillPdfDocument d={data} />} fileName={filename}>
      {({ loading }) => (
        <button
          disabled={loading}
          style={{ width: '100%' }}
          className={[
            'flex items-center justify-center gap-2 px-4 py-3',
            'rounded-xl font-semibold text-sm border-2 transition-all',
            loading
              ? 'bg-ink-100 text-ink-400 border-ink-200 cursor-wait'
              : 'bg-brand-600 text-white border-brand-600 hover:bg-brand-700 active:scale-[0.98] shadow-md',
          ].join(' ')}
        >
          {loading
            ? 'Generating WBSEDCL Bill PDF…'
            : 'Download Bill PDF  —  ' + data.billingMonth}
        </button>
      )}
    </PDFDownloadLink>
  );
}